import { HardHat, ArrowLeft, CheckCircle } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import ContactInfo from "@/components/ContactInfo";
import serviceConstruction from "@/assets/service-construction.jpg";
import { useLanguage } from "@/i18n/LanguageContext";
import SeoMetadata from "@/components/SeoMetadata";

const ConstructionControllers = () => {
  const { t, language } = useLanguage();

  const seoTitle = `${t("construction.pageTitle")} | Gendera Service`;
  const seoDescription = t("service.construction.desc");

  return (
    <div className="min-h-screen bg-background">
      <SeoMetadata title={seoTitle} description={seoDescription} />
      <main className="pt-20">
        <section className="relative h-[400px] overflow-hidden">
          <img
            src={serviceConstruction}
            alt={t("construction.pageTitle")}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary/80" />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4">
              <a
                href={`/${language}#services`}
                className="inline-flex items-center text-accent hover:text-accent/80 mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" size={16} />
                {t("servicePage.backToServices")}
              </a>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-lg bg-accent flex items-center justify-center">
                  <HardHat className="w-8 h-8 text-primary" size={32} />
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-primary-foreground">
                  {t("construction.pageTitle")}
                </h1>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  {t("construction.h2")}
                </h2>
                <p className="text-muted-foreground mb-6">{t("construction.p1")}</p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("construction.whatIsEcuTitle")}
                </h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {t("construction.whatIsEcuText")}
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("construction.ecuResponsibilitiesTitle")}
                </h3>
                <ul className="space-y-3 mb-6">
                  {(["construction.ecuResp1","construction.ecuResp2","construction.ecuResp3","construction.ecuResp4","construction.ecuResp5"] as const).map((key) => (
                    <li key={key} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" size={20} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground mb-8">
                  {t("construction.ecuResp6")}
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("construction.symptomsTitle")}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t("construction.symptomsIntro")}
                </p>
                <ul className="space-y-3 mb-4">
                  {(["construction.symptom1","construction.symptom2","construction.symptom3","construction.symptom4","construction.symptom5","construction.symptom6"] as const).map((key) => (
                    <li key={key} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" size={20} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  {t("construction.symptomsWarning")}
                </p>
              </div>

              <div>
                <img
                  src={serviceConstruction}
                  alt={t("construction.pageTitle")}
                  className="w-full max-w-md h-auto rounded-lg mb-8 shadow-lg mx-auto lg:mx-0"
                />

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("servicePage.scopeOfServices")}
                </h3>
                <ul className="space-y-3 mb-8">
                  {(["construction.scope1","construction.scope2","construction.scope3","construction.scope4","construction.scope5","construction.scope6","construction.scope7"] as const).map((key) => (
                    <li key={key} className="flex items-center gap-3 text-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" size={20} />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("construction.typesTitle")}
                </h3>
                <ul className="space-y-3 mb-8">
                  {(["construction.type1","construction.type2","construction.type3","construction.type4","construction.type5","construction.type6","construction.type7"] as const).map((key) => (
                    <li key={key} className="flex items-center gap-3 text-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" size={20} />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("construction.coverageTitle")}
                </h3>
                <p className="text-muted-foreground">
                  {t("construction.coverageText")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <ContactInfo />
              <ContactForm />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ConstructionControllers;
